/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment } from 'react';
import PropTypes from 'prop-types';

// Component imports
import { t } from 'i18next';
import { Divider, Form, Input, InputNumber, Select, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

// Misc imports
import { DIALOGFLOW_REGIONS } from '../../../constants/main';

// Constant declarations
const { Text } = Typography;
const { Option } = Select;

const DialogflowProviderForm = ({ nlpPipeline }) => {
  return (
    <Fragment>
      <Divider orientation="left" className="mb-8">
        <Text type="secondary" className="fs-base">{t('Provider Settings')}</Text>
      </Divider>

      <Form.Item
        label={t('Project ID')}
        name="projectId"
        required
        rules={[{
          required: true,
          type: 'string',
          whitespace: true
        }]}
      >
        <Input placeholder={t('Enter the project ID')} disabled={nlpPipeline} />
      </Form.Item>

      {
        !nlpPipeline ? (
          <Fragment>
            <Form.Item
              label={t('Private Key')}
              name="privateKey"
              rules={[{
                required: true,
                type: 'string',
                whitespace: true
              }]}
            >
              <Input.TextArea
                autoSize={{ minRows: 6, maxRows: 6 }}
                rows={6}
                placeholder={t('Enter the private key')}
                allowClear
              />
            </Form.Item>

            <Form.Item
              label={t('Private Key ID')}
              name="privateKeyId"
              rules={[{
                required: true,
                type: 'string',
                whitespace: true
              }]}
            >
              <Input placeholder={t('Enter the private key ID')} />
            </Form.Item>
          </Fragment>
        ) : null
      }

      <Form.Item
        label={t('Client ID')}
        name="clientId"
        rules={[{
          required: true,
          type: 'string',
          whitespace: true
        }]}
      >
        <Input placeholder={t('Enter the client ID')} />
      </Form.Item>

      <Form.Item
        label={t('Client Email')}
        name="clientEmail"
        rules={[{
          required: true,
          type: 'string',
          whitespace: true
        }]}
      >
        <Input placeholder={t('Enter the client email')} />
      </Form.Item>

      <Form.Item
        label={t('Token Server URI')}
        name="tokenServerUri"
        rules={[{
          required: true,
          type: 'string',
          whitespace: true
        }]}
      >
        <Input placeholder={t('Enter the token server URI')} />
      </Form.Item>

      <Form.Item
        label={t('Dialogflow Region')}
        name="region"
      >
        <Select
          placeholder={t('Select the Dialogflow region')}
          allowClear
          getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
        >
          {
            Object.keys(DIALOGFLOW_REGIONS).map((region) => {
              return (
                <Option key={region} value={region}>
                  {DIALOGFLOW_REGIONS[region]}
                </Option>
              );
            })
          }
        </Select>
      </Form.Item>
    </Fragment>
  );
};

const DialogflowProviderConfigurationForm = () => {
  return (
    <Form.Item
      label={t('Threshold')}
      name="predictionConfidenceThreshold"
      tooltip={{
        title: t('Threshold value for the confidence score. If the returned value is less than the threshold value, then the Default Flow will be triggered.'),
        icon: <InfoCircleOutlined />
      }}
    >
      <InputNumber
        placeholder="Enter the threshold value"
        min={0}
        max={1}
        step={0.01}
        className="w-100"
      />
    </Form.Item>
  );
};

DialogflowProviderForm.propTypes = {
  nlpPipeline: PropTypes.object
};

export { DialogflowProviderForm, DialogflowProviderConfigurationForm };
